import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import AvatarEditor from 'react-avatar-editor';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';

let count = 1;

export function ImageUploadButton({ onChange, children, ...other }) {
  const [isDirty, setIsDirty] = useState(false);
  const [id] = useState(() => `image-picker-${count++}`);

  const handleChange = useCallback(({ target: { files } }) => {
    if (!files || !files[0]) {
      return;
    }
    
    setImmediate(() => {
      const [file] = files;
      onChange({
        target: {
          value: {
            url: URL.createObjectURL(file),
            file,
          }
        }
      });
    });
    setIsDirty(true);
  }, [onChange, setIsDirty]);

  return (
    <label {...other} htmlFor={id}>
      <HiddenInput
        {...{ id }}
        onChange={handleChange}
      />
      {children}
    </label>
  );
}

export default function ImagePicker({ value, onChange, onFile, label, size = 100, children = defaultChildren, forceDirty, ...other }) {
  const [id] = useState(() => `image-picker-${count++}`);
  const editor = useRef();
  const [isDirty, setIsDirty] = useState(false);
  const getImage = useCallback(() => new Promise((resolve, reject) => {
    try {
      if (editor.current) {
        const canvasScaled = editor.current.getImageScaledToCanvas();
        canvasScaled.toBlob(blob => {
          blob.name = 'logo.jpg';
          resolve(blob);
          setIsDirty(false);
        }, 'image/jpeg');
      } else if (!isDirty) {
        resolve(null);
      } else {
        reject(new Error('no editor'));
      }
    } catch (err) {
      reject(err);
    }
  }), [editor.current, isDirty, setIsDirty]);
  const handleChange = useCallback(({ target: { files } }) => {
    if (!files || !files[0]) {
      return;
    }
    
    setImmediate(() => {
      const [file] = files;
      onChange({
        target: {
          value: URL.createObjectURL(file),
        },
      });
    });
    setIsDirty(true);
  }, [onChange, setIsDirty]);
  useEffect(() => {
    onFile(() => getImage);
  }, [onFile, getImage]);
  const [zoom, setZoom] = useState(1);
  const handleZoomChange = useCallback((e, zoom) => {
    setZoom(zoom);
  }, [setZoom]);

  return (
    <>
      <HiddenInput
       {...{ id }}
       onChange={handleChange}/>
      <Row >
        {label}
        <Frame show={isDirty} {...other}>
          <div>
            {isDirty || forceDirty || !value
              ? <Editor
                  ref={editor}
                  disabled={!isDirty}
                  image={value}
                  scale={zoom}
                  width={size}
                  height={size}
                  border={size / 4}
                  borderRadius={size / 4}              
                />
              : <RoundedImage
                  src={value}
                  {...{ size }}
                />}
            <ZoomContainer show={isDirty || forceDirty} {...{ size }}>
              <Zoom
                value={zoom}
                aria-labelledby="Zoom"
                onChange={handleZoomChange}
              />
            </ZoomContainer>
          </div>
          <Label htmlFor={id}>
            {children}
          </Label>
        </Frame>
      </Row>
    </>
  );
}

const Frame = styled.div`
  border-width: 2px;
  border-style: dashed;
  border-color: ${({ show }) => show ? 'rgba(0,0,0,.2)' : 'transparent'}
`;

const RoundedImage = styled.img`
  border-radius: 3px;
  /*width: ${({ size }) => size}px;*/
  /*height: ${({ size }) => size}px;
  border: 1px solid #CBD6E2;*/
  object-fit: cover;
  margin-top: 30px;
`;

const Editor = styled(AvatarEditor).attrs({
  color: [255,255,255,.6],
  crossOrigin: 'anonymous',
})`
`;

const HiddenInput = styled.input.attrs({
  type: 'file',
  accept: 'image/*',
})`
  display: none;
`;

const Label = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ZoomContainer = styled.div`
  width: ${({ size }) => size * 1.5}px;
  ${({ show }) => !show && 'visibility: hidden;'}
`;

const Zoom = styled(Slider).attrs({
  min: 0.5,
  max: 3,
  step: 0.1,
})`
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const defaultChildren = (
  <Button component="span">
    Change Image
  </Button>
);